import React from 'react';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';
import { Mail, Lightbulb, Scale, Users, Code } from 'lucide-react';

const CareersPage = () => {
  const values = [
    {
      title: 'Ownership',
      description: 'Take responsibility for outcomes, not just tasks.'
    },
    {
      title: 'Clarity',
      description: 'Communicate simply. Avoid jargon. Make things understandable.'
    },
    {
      title: 'Accountability',
      description: 'Do what you say. Follow through. Be reliable.'
    },
    {
      title: 'Ethics',
      description: 'Do the right thing, even when no one is watching.'
    }
  ];

  const roles = [
    {
      icon: Scale,
      title: 'Compliance',
      description: 'Help businesses navigate regulations. Review filings, handle escalations, ensure accuracy.'
    },
    {
      icon: Lightbulb,
      title: 'Product',
      description: 'Design experiences that make compliance simple. Research, prototype, iterate.'
    },
    {
      icon: Code,
      title: 'Engineering',
      description: 'Build reliable systems. Work with AI, automation, and secure infrastructure.'
    },
    {
      icon: Users,
      title: 'Operations',
      description: 'Keep things running smoothly. Support customers, manage workflows, improve processes.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Careers at ComplyKit
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            We're building the compliance platform India deserves—one that's clear, 
            reliable, and actually helpful.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h2 className="text-2xl font-semibold mb-4 text-slate-900">What we're building</h2>
          <p className="text-slate-600 leading-relaxed">
            Compliance in India is complex, fragmented, and often frustrating. Millions of people 
            and businesses struggle with regulations they don't fully understand, deadlines they 
            can't track, and professionals they can't always trust. We're changing that.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            ComplyKit combines AI automation with expert oversight to make compliance accessible, 
            transparent, and stress-free. We're not just building software—we're building trust.
          </p>
        </div>
      </section>

      {/* Who we look for */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h2 className="text-2xl font-semibold mb-4 text-slate-900">Who we look for</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            We hire people who care about doing good work. You don't need a fancy resume or 
            a specific background. What matters is:
          </p>
          <ul className="space-y-3 text-slate-600">
            <li className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
              <span>You solve problems thoughtfully, not just quickly</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
              <span>You communicate clearly and listen well</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
              <span>You take ownership and follow through</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
              <span>You're comfortable with ambiguity and change</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
              <span>You care about the impact of your work</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h2 className="text-2xl font-semibold mb-8 text-slate-900">How we work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="p-5 border border-slate-200 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">{value.title}</h3>
                <p className="text-sm text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h2 className="text-2xl font-semibold mb-8 text-slate-900">Teams we're building</h2>
          <div className="space-y-4">
            {roles.map((role, index) => (
              <div key={index} className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-lg">
                <role.icon className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{role.title}</h3>
                  <p className="text-sm text-slate-600">{role.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="p-8 bg-slate-900 rounded-xl text-center">
            <Mail className="h-8 w-8 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-white mb-3">Interested?</h2>
            <p className="text-slate-300 mb-6 max-w-md mx-auto">
              We don't always have open positions listed, but we're always looking for 
              good people. Tell us about yourself.
            </p>
            <a 
              href="mailto:careers@complykit.in" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-medium rounded-lg hover:bg-slate-100 transition-colors"
            >
              careers@complykit.in
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
